/**
 * Render a Mermaid diagram inside a markdown document (RFC 0263 viewer).
 *
 * A ```mermaid fenced block in a document renders as an SVG diagram instead of a
 * code block. Mermaid is heavy, so it is dynamically imported the first time a
 * diagram is actually shown (a document with no diagrams never loads it). A
 * render failure falls back to the diagram source, so a malformed diagram is
 * still readable rather than blank.
 */

import {
  isValidElement,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react'

// A process-wide counter for the unique id mermaid.render needs per call (a
// stable, valid DOM id; useId's colons are not valid selectors).
let renderSeq = 0

export function MermaidDiagram({ chart }: { chart: string }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const mermaid = (await import('mermaid')).default
        // Match the app's light/dark theme (tailwind's `dark` class on <html>).
        const dark = document.documentElement.classList.contains('dark')
        mermaid.initialize({
          startOnLoad: false,
          // Strict: sanitize labels and block script/HTML injection, since the
          // diagram source is arbitrary document content.
          securityLevel: 'strict',
          theme: dark ? 'dark' : 'default',
        })
        renderSeq += 1
        const { svg } = await mermaid.render(`mermaid-${renderSeq}`, chart)
        if (cancelled) return
        setError(null)
        if (containerRef.current) containerRef.current.innerHTML = svg
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e))
      }
    })()
    return () => {
      cancelled = true
    }
  }, [chart])

  if (error) {
    return (
      <pre className="overflow-x-auto rounded bg-muted/60 px-3 py-2 text-xs">
        <code>{chart}</code>
      </pre>
    )
  }
  return (
    <div
      ref={containerRef}
      className="my-4 flex justify-center overflow-x-auto"
      role="img"
      aria-label="diagram"
    />
  )
}

/**
 * If `children` is a ```mermaid fenced code block (the `<code>` element
 * react-markdown produces inside a `<pre>`), return its source; otherwise null.
 * Pure, so the routing that decides diagram-vs-code-block is unit-testable
 * without rendering mermaid.
 */
export function mermaidChartFrom(children: ReactNode): string | null {
  if (!isValidElement(children)) return null
  const props = children.props as { className?: unknown; children?: unknown }
  const className = typeof props.className === 'string' ? props.className : ''
  if (!/(?:^|\s)language-mermaid(?:\s|$)/.test(className)) return null
  const raw = props.children
  const text = typeof raw === 'string' ? raw : String(raw ?? '')
  return text.replace(/\n$/, '')
}
